import { useState, type ReactNode } from "react"

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react"
import {
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
} from "@heroicons/react/20/solid"
import format from "date-fns/format"
import parseISO from "date-fns/parseISO"

import type {
  SolarAuroraActiveDesignDto,
  SolarAuroraProjectDesignDto,
  SolarAuroraWarning,
  SolarProposalAuroraDto,
} from "~/api/dto/solar-proposal-aurora.dto"
import useSolarAuroraCommand from "~/api/hooks/useSolarAuroraCommand"
import useSolarAuroraProjectDesigns from "~/api/hooks/useSolarAuroraProjectDesigns"
import useSolarProposalAurora from "~/api/hooks/useSolarProposalAurora"
import useSolarProposalDesign from "~/api/hooks/useSolarProposalDesign"
import { solarProposalErrorMessages } from "~/api/solar-proposal-errors"
import { buildAuroraCadUrl } from "~/utils/aurora-design"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { SolarDesignChangeOutcome } from "../SolarDesignChangeOutcome"

export const AURORA_NOT_LINKED_NOTICE = `An administrator must link the first Aurora design.`
export const AURORA_SCREENSHOT_NOT_CAPTURED_NOTICE = `Aurora's CAD screenshot couldn't be saved, so the proposal has no system image yet.`

const NOT_SET = `—`

const sectionTitleClass = `text-xs font-semibold tracking-wide text-muted uppercase`
const secondaryButtonClass = `inline-flex items-center justify-center gap-1.5 rounded-md border border-control-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50`
const primaryButtonClass = `inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50`

type Tabs = `details` | `versions`
const TABS: ReadonlyArray<{ id: Tabs; label: string }> = [
  { id: `details`, label: `Details` },
  { id: `versions`, label: `Versions` },
]

const formatTimestamp = (timestamp: string | null): string =>
  timestamp ? format(parseISO(timestamp), `MMM d, yyyy · h:mm a`) : NOT_SET

const formatDate = (timestamp: string | null): string | null =>
  timestamp ? format(parseISO(timestamp), `MMM d, yyyy`) : null

const formatKw = (kw: number | null): string | null =>
  kw == null ? null : `${kw.toFixed(2)} kW`

const Notice = ({
  children,
  locked = false,
}: {
  children: ReactNode
  locked?: boolean
}): JSX.Element => (
  <p
    role="status"
    className="flex items-start gap-2 rounded-xl border border-border bg-surface p-4 text-sm text-foreground"
  >
    {locked ? (
      <LockClosedIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-muted"
      />
    ) : null}
    {children}
  </p>
)

const SectionError = ({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}): JSX.Element => (
  <div
    role="alert"
    className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
  >
    <p className="text-sm text-foreground">{message}</p>
    <button
      type="button"
      className="text-sm font-semibold text-primary underline"
      onClick={onRetry}
    >
      Retry
    </button>
  </div>
)

const CommandError = ({ error }: { error: unknown }): JSX.Element => {
  const messages = solarProposalErrorMessages(error)
  return (
    <div
      role="alert"
      className="space-y-1 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-foreground"
    >
      {messages.length > 0 ? (
        messages.map((message) => <p key={message}>{message}</p>)
      ) : (
        <p>
          {parseAxiosError(error, `The Aurora design could not be updated.`)}
        </p>
      )}
    </div>
  )
}

const DetailRow = ({
  label,
  value,
}: {
  label: string
  value: string
}): JSX.Element => (
  <div className="flex justify-between gap-4 py-3">
    <dt className="text-muted">{label}</dt>
    <dd className="text-right font-semibold break-words text-foreground">
      {value}
    </dd>
  </div>
)

const Details = ({
  design,
  canEdit,
  refreshDisabled,
  refreshing,
  onRefresh,
}: {
  design: SolarAuroraActiveDesignDto | null
  canEdit: boolean
  refreshDisabled: boolean
  refreshing: boolean
  onRefresh: () => void
}): JSX.Element => {
  if (!design) {
    return <Notice>This proposal has no active Aurora design.</Notice>
  }
  const cadUrl = buildAuroraCadUrl(design.auroraProjectId, design.id)

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Active design" className="flex flex-col gap-3">
        <h3 className={sectionTitleClass}>Active design</h3>
        <dl className="divide-y divide-border rounded-xl border border-border px-4 text-sm">
          <DetailRow label="Name" value={design.name ?? NOT_SET} />
          <DetailRow
            label="Created in Aurora"
            value={formatDate(design.auroraCreatedAt) ?? NOT_SET}
          />
          <DetailRow
            label="System size"
            value={formatKw(design.systemSizeKw) ?? NOT_SET}
          />
          <DetailRow
            label="Annual production"
            value={
              design.annualProductionKwh == null
                ? NOT_SET
                : `${Math.round(design.annualProductionKwh).toLocaleString(`en-US`)} kWh`
            }
          />
          <DetailRow
            label="Last synced"
            value={formatTimestamp(design.syncCompletedAt)}
          />
        </dl>
      </section>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          className={primaryButtonClass}
          disabled={refreshDisabled}
          onClick={onRefresh}
        >
          {refreshing ? `Refreshing…` : `Refresh from Aurora`}
        </button>
        {canEdit && cadUrl ? (
          <a
            className={secondaryButtonClass}
            href={cadUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Edit in Aurora
            <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-4" />
          </a>
        ) : (
          <button type="button" className={secondaryButtonClass} disabled>
            Edit in Aurora
            <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
    </div>
  )
}

const describeDesign = (design: SolarAuroraProjectDesignDto): string =>
  [formatDate(design.createdAt), formatKw(design.systemSizeKw)]
    .filter(Boolean)
    .join(` · `)

const Versions = ({
  proposalId,
  actionsDisabled,
  switching,
  onSwitch,
}: {
  proposalId: string
  actionsDisabled: boolean
  switching: boolean
  onSwitch: (design: SolarAuroraProjectDesignDto) => void
}): JSX.Element => {
  const query = useSolarAuroraProjectDesigns(proposalId, { enabled: true })
  const [pending, setPending] = useState<SolarAuroraProjectDesignDto | null>(
    null,
  )

  if (query.isError) {
    return (
      <SectionError
        message={parseAxiosError(
          query.error,
          `The Aurora project's designs could not be loaded.`,
        )}
        onRetry={() => void query.refetch()}
      />
    )
  }
  if (!query.data) {
    return (
      <div
        role="status"
        aria-label="Loading Aurora designs"
        className="h-32 animate-pulse rounded-xl bg-muted"
      />
    )
  }
  if (query.data.status === `aurora_not_linked`) {
    return <Notice>{AURORA_NOT_LINKED_NOTICE}</Notice>
  }

  const { designs, linkBlockedReason } = query.data
  const disabled = actionsDisabled || linkBlockedReason !== null

  return (
    <div className="flex flex-col gap-4">
      {linkBlockedReason ? <Notice locked>{linkBlockedReason}</Notice> : null}
      {designs.length === 0 ? (
        <p className="text-sm text-muted">
          Aurora lists no designs in this project.
        </p>
      ) : (
        <ul
          aria-label="Aurora designs"
          className="divide-y divide-border rounded-xl border border-border"
        >
          {designs.map((design) => (
            <li
              key={design.id}
              className="flex items-center justify-between gap-4 p-4 text-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-semibold text-foreground">
                  {design.name}
                </p>
                <p className="text-muted">{describeDesign(design)}</p>
              </div>
              {design.active ? (
                <span className="shrink-0 rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">
                  Active
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`${design.linked ? `Switch to` : `Link`} ${design.name}`}
                  className={secondaryButtonClass}
                  disabled={disabled}
                  onClick={() => setPending(design)}
                >
                  {design.linked ? `Switch` : `Link`}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {pending ? (
        <div
          aria-live="polite"
          className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm text-foreground"
        >
          <p className="font-semibold">
            {pending.linked ? `Switch to` : `Link`} {pending.name}?
          </p>
          <p>
            The design, its equipment and production change now. Pricing and
            savings stay as they are until you refresh them.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className={secondaryButtonClass}
              disabled={switching}
              onClick={() => setPending(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              disabled={disabled}
              onClick={() => {
                onSwitch(pending)
                setPending(null)
              }}
            >
              {pending.linked ? `Switch version` : `Link version`}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const LinkedAurora = ({
  aurora,
  proposalId,
  viewerCanUpdate,
  linkWarnings,
}: {
  aurora: Extract<SolarProposalAuroraDto, { status: `linked` }>
  proposalId: string
  viewerCanUpdate: boolean
  linkWarnings: SolarAuroraWarning[] | null
}): JSX.Element => {
  const command = useSolarAuroraCommand()
  const [tab, setTab] = useState<Tabs>(`details`)
  // Versions mounts on first visit (its listing is an Aurora call) and stays mounted.
  const [versionsVisited, setVersionsVisited] = useState(false)
  // A first link just made is a design change like a switch.
  const [changed, setChanged] = useState(linkWarnings !== null)
  // A link, switch or refresh that missed production says so at once; the
  // design read's production status keeps its Retry across reopening the step.
  const [productionMissed, setProductionNotUpdated] = useState(
    linkWarnings?.includes(`production_not_updated`) ?? false,
  )
  // Each later switch or refresh says whether the design has a CAD screenshot.
  const [screenshotNotCaptured, setScreenshotNotCaptured] = useState(
    linkWarnings?.includes(`screenshot_not_captured`) ?? false,
  )
  const design = useSolarProposalDesign(proposalId)
  const productionNotUpdated =
    productionMissed || design.data?.productionStatus === `not_updated`

  const actionsDisabled =
    !viewerCanUpdate || aurora.financingLocked || command.isLoading

  const run = (
    variables: Parameters<typeof command.mutate>[0],
    onDone?: () => void,
  ): void =>
    command.mutate(variables, {
      onSuccess: (saved) => {
        setChanged(true)
        setProductionNotUpdated(
          saved.warnings.includes(`production_not_updated`),
        )
        setScreenshotNotCaptured(
          saved.warnings.includes(`screenshot_not_captured`),
        )
        onDone?.()
      },
    })

  const selectTab = (index: number): void => {
    const next = TABS[index]?.id ?? `details`
    setTab(next)
    if (next === `versions`) setVersionsVisited(true)
  }

  return (
    <div className="flex flex-col gap-5">
      {aurora.financingLocked ? (
        <Notice locked>
          {aurora.financingLockReason ?? `Financing locks the design.`}
        </Notice>
      ) : null}
      {screenshotNotCaptured ? (
        <Notice>{AURORA_SCREENSHOT_NOT_CAPTURED_NOTICE}</Notice>
      ) : null}
      {changed || productionNotUpdated ? (
        <SolarDesignChangeOutcome
          proposalId={proposalId}
          productionNotUpdated={productionNotUpdated}
          onProductionUpdated={() => setProductionNotUpdated(false)}
          testId="solar-aurora-out-of-date"
        />
      ) : null}
      {command.isError ? <CommandError error={command.error} /> : null}
      <TabGroup
        selectedIndex={TABS.findIndex(({ id }) => id === tab)}
        onChange={selectTab}
        className="flex flex-col gap-5"
      >
        <TabList
          aria-label="Aurora design"
          className="flex gap-1 border-b border-border"
        >
          {TABS.map(({ id, label }) => (
            <Tab
              key={id}
              className="group relative px-4 py-3 text-sm font-semibold text-muted transition-colors hover:text-foreground focus:outline-none data-focus:outline-2 data-focus:outline-focus-ring data-selected:text-primary"
            >
              {label}
              <span
                aria-hidden="true"
                className="absolute inset-x-3 bottom-0 hidden h-0.5 rounded-full bg-primary group-data-selected:block"
              />
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel unmount={false} className="focus:outline-none">
            <Details
              design={aurora.activeDesign}
              canEdit={viewerCanUpdate}
              refreshDisabled={actionsDisabled}
              refreshing={
                command.isLoading && command.variables?.kind === `refresh`
              }
              onRefresh={() => run({ kind: `refresh`, proposalId })}
            />
          </TabPanel>
          <TabPanel unmount={false} className="focus:outline-none">
            {versionsVisited ? (
              <Versions
                proposalId={proposalId}
                actionsDisabled={actionsDisabled}
                switching={command.isLoading}
                onSwitch={(design) =>
                  run(
                    { kind: `switch`, proposalId, auroraDesignId: design.id },
                    () => setTab(`details`),
                  )
                }
              />
            ) : null}
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  )
}

/**
 * The System Design step's Aurora drawer body for Solar Proposal V2, after
 * V1's Aurora Design drawer: Details (the active design, Refresh from Aurora,
 * Edit in Aurora) and Versions (the Aurora project's designs to link or
 * switch to). It renders inside the step's own drawer chrome. A refresh or a
 * switch runs inline and recalculates neither pricing nor savings; both then
 * read out of date. A financing lock, or a viewer without update rights,
 * leaves it read-only. Right after a first link, `linkWarnings` carries what
 * the link couldn't finish.
 */
export const SolarAuroraDesignDrawer = ({
  proposalId,
  viewerCanUpdate,
  linkWarnings = null,
}: {
  proposalId: string
  viewerCanUpdate: boolean
  linkWarnings?: SolarAuroraWarning[] | null
}): JSX.Element => {
  const query = useSolarProposalAurora(proposalId)

  return (
    <div className="flex flex-col gap-5" data-testid="solar-aurora-design">
      {query.data ? (
        query.data.status === `aurora_not_linked` ? (
          <Notice>{AURORA_NOT_LINKED_NOTICE}</Notice>
        ) : (
          <LinkedAurora
            aurora={query.data}
            proposalId={proposalId}
            viewerCanUpdate={viewerCanUpdate}
            linkWarnings={linkWarnings}
          />
        )
      ) : query.isError ? (
        <SectionError
          message={parseAxiosError(
            query.error,
            `The Aurora design could not be loaded.`,
          )}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <div
          role="status"
          aria-label="Loading Aurora design"
          className="h-40 animate-pulse rounded-xl bg-muted"
        />
      )}
    </div>
  )
}
